import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import DACoreLogo from '@wix/da-ds/pkg/DACoreLogo';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { PapiSubscription } from '@wix/da-papi-types';
import { type PackagePickerStoreSlice } from '../../../../../redux/packagePicker/types';
import PlanExpiringNote from './PlanExpiringNote';
import GiftRecipient from './GiftRecipient';

import s from './HeroHeader.scss';

export interface Props {
  hasCore?: boolean;
  showSubtitle?: boolean;
  giftUser?: PackagePickerStoreSlice['giftUser'];
  sale?: PackagePickerStoreSlice['sale'];
  currentCoreSubscription?: PapiSubscription;
}

const HeroHeader: React.FC<Props> = ({
  hasCore,
  giftUser,
  currentCoreSubscription,
  showSubtitle = true,
}) => {
  const { t } = useTranslation();
  const isDesktop = !useContext(MobileContext);
  const planWillExpire = currentCoreSubscription?.willExpire;

  if (giftUser) {
    return (
      <div className={s['root']}>
        <Heading className={s['header']}>
          {t('packagePicker.heroTable.header.gift')}
        </Heading>
        <GiftRecipient user={giftUser} />
        <div className={s['manage-info']}>
          {t('packagePicker.heroTable.header.gift.below')}
        </div>
      </div>
    );
  }

  if (!hasCore && currentCoreSubscription === undefined) {
    return (
      <div className={s['root']}>
        <Heading className={s['header']}>
          <Trans
            i18nKey={`packagePicker.heroTable.header.noCoreWithLogo${
              isDesktop ? '' : '.mobile'
            }`}
          >
            Upgrade to a <DACoreLogo className={s['core-logo']} /> Plan
          </Trans>
        </Heading>
        {showSubtitle && (
          <Heading className={s['subheader']}>
            {t('packagePicker.heroTable.header.noCore.info')}
          </Heading>
        )}
      </div>
    );
  }

  return (
    <div className={s['root']}>
      {planWillExpire && (
        <PlanExpiringNote endTimestamp={currentCoreSubscription!.endts!} />
      )}
      <Heading className={s['header']}>
        <Trans
          i18nKey={`packagePicker.heroTable.header.upgradeWithLogo${
            isDesktop ? '' : '.mobile'
          }`}
        >
          Upgrade Your <DACoreLogo className={s['core-logo']} /> Plan
        </Trans>
      </Heading>
      {showSubtitle && (
        <Heading className={s['subheader']}>
          {t(
            `packagePicker.heroTable.header.upgrade.infoV2${
              isDesktop ? '' : '.mobile'
            }`
          )}
        </Heading>
      )}
    </div>
  );
};
HeroHeader.displayName = 'HeroHeader';

export default HeroHeader;
